import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
  documentId,
} from "firebase/firestore";
import { db } from "./firebase";
import { CLUB_ID, THEME_COLOR } from "./config/club";
import MembersRanking from "./components/MembersRanking";
import { useAuth } from "./context/AuthContext";
import React from "react";
import BottomNav from "./components/layout/BottomNav";
import AppHeader from "./components/layout/AppHeader"
import { useNavigate } from "react-router-dom";

import { BOTTOM_NAV_HEIGHT } from "./config/club";
import GlobalAdsModal from "./components/ads/GlobalAdsModal";

const PAGE_SIZE = 15;
const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth();
const monthOptions = Array.from({ length: currentMonth + 1 }, (_, i) => {
  const date = new Date(currentYear, i);
  const label = date.toLocaleString('en-US', { month: 'short', year: 'numeric' });
  // Matches your "YYYY-MM" format (0-indexed months)
  const value = `${currentYear}-${String(i).padStart(2, '0')}`;

  return { value, label };
}).reverse();
export default function OverallRankings() {
  const THEME_VIOLET = THEME_COLOR;

  const [matches, setMatches] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const [selectedFilter, setSelectedFilter] = useState(`${currentYear}-${String(currentMonth).padStart(2, '0')}`);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  // 1. Create a ref for the observer
  const observer = useRef();

  /* ================= LOAD MATCHES ================= */
  useEffect(() => {
    loadMatches();
  }, []);

  const loadMatches = async () => {
    try {
      setLoading(true);
      const snap = await getDocs(
        query(
          collection(db, "clubs", CLUB_ID, "matchHistory"),
          orderBy("playedAt", "desc")
        )
      );
      setMatches(snap.docs.map((d) => d.data()));
    } catch (err) {
      console.error("Failed to load matches", err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= COMPUTE + RANK ================= */
  useEffect(() => {
    if (!matches.length) return;

    let filtered = matches;

    if (selectedFilter !== "overall") {
      const [year, month] = selectedFilter.split("-").map(Number);
      filtered = matches.filter((m) => {
        if (!m.playedAt) return false;
        const d = m.playedAt.toDate();
        return d.getFullYear() === year && d.getMonth() === month;
      });
    }

    const base = buildOverallRankings(filtered);

    // 🔑 ASSIGN GLOBAL RANK ONCE
    const ranked = base.map((p, index) => ({
      ...p,
      rank: index + 1,
    }));

    setRankings(ranked);
    setPage(1);
  }, [matches, selectedFilter]);

  /* ================= MERGE USER DATA ================= */
  const mergedRankings = useMemo(() => {
    return rankings.map((p) => {
      const user = usersMap[p.id];
      return {
        ...p,
        displayName: user?.displayName || user?.username || p.name,
        username: user?.username || null,
        photoURL: user?.photoURL || null,
      };
    });
  }, [rankings, usersMap]);

  /* ================= SEARCH + PAGINATION ================= */
  // 1. Get the fully filtered list first (Search + Membership Filter)
  const filteredList = useMemo(() => {
    let list = mergedRankings;

    if (search.trim()) {
      list = list.filter((p) =>
        (p.displayName || "")
          .toLowerCase()
          .includes(search.toLowerCase())
      );
    }
    return list;
  }, [mergedRankings, search]);
  // const visibleRankings = useMemo(() => {

  //   let list = mergedRankings;

  //   if (search.trim()) {
  //     list = list.filter((p) =>
  //       (p.displayName || "")
  //         .toLowerCase()
  //         .includes(search.toLowerCase())
  //     );
  //   }

  //   return list.slice(0, page * PAGE_SIZE);
  // }, [mergedRankings, search, page]);
  const visibleRankings = useMemo(() => {
    return filteredList.slice(0, page * PAGE_SIZE);
  }, [filteredList, page]);
  /* ================= CHEAP USER FETCH ================= */


  const fetchUsersForRankings = useCallback(async (list) => {
    const ids = list.map((r) => r.id);
    const missingIds = ids.filter((id) => !usersMap[id]);
    if (!missingIds.length) return;

    const batches = [];
    for (let i = 0; i < missingIds.length; i += 10) {
      batches.push(missingIds.slice(i, i + 10));
    }

    const newUsers = {};

    for (const batch of batches) {
      const snap = await getDocs(
        query(collection(db, "users"), where(documentId(), "in", batch))
      );

      snap.forEach((doc) => {
        newUsers[doc.id] = doc.data();
      });
    }

    setUsersMap((prev) => ({ ...prev, ...newUsers }));
  }, [usersMap]);
  useEffect(() => {
    if (visibleRankings.length > 0) {
      fetchUsersForRankings(visibleRankings);
    }
  }, [visibleRankings, fetchUsersForRankings]);

  // 2. Intersection Observer Callback
  // This ref is attached to the last element of the list
  const lastElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
      // COMPARE AGAINST filteredList, NOT mergedRankings
      if (entries[0].isIntersecting && visibleRankings.length < filteredList.length) {
        setPage(prevPage => prevPage + 1);
      }
    });

    if (node) observer.current.observe(node);
  }, [loading, visibleRankings.length, filteredList.length]); // Added filteredList here


  // Monitor scroll position
  useEffect(() => {
    const handleScroll = () => {
      // Show button after scrolling down 400px
      if (window.scrollY > 400) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  //console.log(visibleRankings);
  return (
    <div
      className="min-vh-100 pb-5"
      style={{
        backgroundColor: "var(--app-bg)",
        marginBottom: 30
      }}
    >
      {/* HEADER */}
      <AppHeader
        user={user}
        title={"Club Rankings"}
        subtitle={selectedFilter === "overall"
          ? "All-time leaderboard"
          : "Monthly Standing"}
        clubId={CLUB_ID}
        sessionId={null}
        logout={logout}
      />

      <div className="container px-3" style={{ maxWidth: 500, marginTop: 30 }}>
        {/* <GlobalAdsModal
          clubId={CLUB_ID}
          themeColor={THEME_VIOLET}
          sessionKey={null}
        /> */}
        {/* CONTROLS */}
        <div className="row g-2 mb-2">
          <div className="col-8">
            <input
              className="form-control form-control-sm"
              style={{
                backgroundColor: "var(--card-bg)",
                color: "var(--text-color)",
                border: "1px solid var(--border-color)"
              }}
              placeholder="Find player..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="col-4">
            <select
              className="form-select form-select-sm"
              style={{
                backgroundColor: "var(--card-bg)",
                color: "var(--text-color)",
                border: "1px solid var(--border-color)"
              }}
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
            >
              <option value="overall">Overall</option>
              {monthOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <p className="small" style={{ color: "var(--muted-text)" }}>
          Minimum 5 games required to appear in rankings
        </p>

        {loading && page === 1 ? (
          <div className="d-flex align-items-center justify-content-center py-5">
            <div className="spinner-border" style={{ color: THEME_VIOLET }} />
          </div>
        ) : (
          <>
            {visibleRankings.length !== 0 && (
              <MembersRanking members={visibleRankings} currentUserId={user?.uid} THEME_COLOR={THEME_COLOR} />
            )}

            {/* 3. The Infinite Scroll Sentinel */}
            {/* ONLY show the observer sentinel if there are more filtered items to load */}
            {visibleRankings.length < filteredList.length ? (
              <div
                ref={lastElementRef}
                className="d-flex justify-content-center py-4"
              >
                <div
                  className="spinner-border spinner-border-sm"
                  style={{ color: THEME_VIOLET }}
                />
              </div>
            ) : (
              /* Show 'end of list' only if we actually have results */
              visibleRankings.length > 0 && (
                <p
                  className="text-center small mt-4"
                  style={{ color: "var(--muted-text)", opacity: 0.6 }}
                >
                  — You've reached the end —
                </p>
              )
            )}
            {/* Handle No Results Found */}
            {visibleRankings.length === 0 && search && (
              <div className="text-center py-5">
                <i
                  className="bi bi-search fs-1"
                  style={{ color: "var(--muted-text)", opacity: 0.3 }}
                ></i>

                <p style={{ color: "var(--muted-text)" }} className="mt-2">No players found matching "{search}"</p>
              </div>
            )}
              {visibleRankings.length === 0 && !search && (
                <div className="text-center py-5 px-4 rounded-4">
                  <div className="mb-3" style={{ fontSize: '3rem' }}>🏆</div>

                  <h5 className="fw-bold mb-1">Fresh Season Started!</h5>
                  <p className="small mb-4" style={{ color: "var(--muted-text)", maxWidth: '250px', margin: '0 auto' }}>
                    The monthly rankings have reset. Play your first match to claim the top spot!
                  </p>
                </div>
              )}
          </>
        )}
      </div>
      {/* SCROLL TO TOP BUTTON */}
      <button
        onClick={scrollToTop}
        className={`btn shadow-lg rounded-circle position-fixed d-flex align-items-center justify-content-center transition-all ${showScrollTop ? "opacity-100 scale-100" : "opacity-0 scale-0"
          }`}
        style={{
          bottom: `calc(${BOTTOM_NAV_HEIGHT}px + 20px)`, // Sits just above BottomNav
          right: "20px",
          width: "45px",
          height: "45px",
          backgroundColor: THEME_COLOR,
          color: "white",
          zIndex: 1050,
          border: "none",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          pointerEvents: showScrollTop ? "auto" : "none",
        }}
      >
        <i className="bi bi-arrow-up fs-5"></i>
      </button>
      <BottomNav />
      {/* Add this simple CSS for the scale effect if not using a library */}
      <style>{`
        .scale-100 { transform: scale(1); }
        .scale-0 { transform: scale(0); }
        .transition-all { transition: all 0.3s ease; }
      `}</style>
    </div>
  );
}

/* ================= HELPERS ================= */
function buildOverallRankings(matches) {
  const map = {};

  matches.forEach((m) => {
    if (!m.score || !m.players) return;

    const [a, b] = m.score.split("-").map(Number);
    const diff = Math.abs(a - b);
    const teamAWin = a > b;

    const teamA = m.players.slice(0, 2);
    const teamB = m.players.slice(2, 4);

    [...teamA, ...teamB].forEach((p) => {
      if (!map[p.id]) {
        map[p.id] = {
          id: p.id,
          name: p.name,
          gamesPlayed: 0,
          wins: 0,
          losses: 0,
          points: 0,
        };
      }
      map[p.id].gamesPlayed++;
    });

    teamA.forEach((p) => {
      if (teamAWin) {
        map[p.id].wins++;
        map[p.id].points += diff;
      } else map[p.id].losses++;
    });

    teamB.forEach((p) => {
      if (!teamAWin) {
        map[p.id].wins++;
        map[p.id].points += diff;
      } else map[p.id].losses++;
    });
  });

  return Object.values(map)
    .filter((p) => p.gamesPlayed >= 5)
    .sort(
      (a, b) =>
        b.wins - a.wins ||
        b.points - a.points ||
        a.losses - b.losses
    );
}
